/**
 * Synth Behavior
 *
 * Oscillator-based tone generator using the Web Audio API.
 * Supports MIDI note numbers and direct frequencies, ADSR envelope shaping,
 * polyphonic voices, and 3D spatial audio via PannerNode.
 */

import { Observer } from "@babylonjs/core/Misc/observable";
import { Scene } from "@babylonjs/core/scene";
import { TraceBehavior } from "./tracebehavior";
import { SynthData, SynthWaveform } from "../scenedata/behaviors/synthdata";
import { ValidationError } from "../scenedata/behaviordata";
import type { AudioManager } from "../managers/AudioManager";

/** Options for playNote / playFrequency that can override behavior defaults */
export interface PlayToneOptions {
    duration?: number;
    volume?: number;
    waveform?: SynthWaveform;
}

/** Internal voice tracking */
interface Voice {
    oscillator: OscillatorNode;
    gain: GainNode;
    panner: PannerNode | null;
    releaseTimeout: number | null;
}

/** Convert MIDI note number to frequency in Hz */
function midiToFrequency(note: number): number {
    return 440 * Math.pow(2, (note - 69) / 12);
}

export class Synth extends TraceBehavior<SynthData> {
    static readonly behaviorType = "Synth" as const;

    private _audioManager: AudioManager | null = null;
    private _activeVoices: Set<Voice> = new Set();
    private _positionObserver: Observer<Scene> | null = null;
    private _lastPosX = 0;
    private _lastPosY = 0;
    private _lastPosZ = 0;

    protected onDetach(): void {
        this._stopAll();
        this._removePositionObserver();
        this._audioManager = null;
    }

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            if (this._resolved.playOnStart) {
                this._playDefault();
            }
        } else {
            this._stopAll();
        }
    }

    protected validate(data: SynthData): ValidationError[] {
        const errors: ValidationError[] = [];
        if (data.note !== undefined && (data.note < 0 || data.note > 127)) {
            errors.push({ field: 'note', message: 'MIDI note must be 0–127' });
        }
        if (data.frequency !== undefined && (data.frequency < 20 || data.frequency > 20000)) {
            errors.push({ field: 'frequency', message: 'Frequency must be 20–20000 Hz' });
        }
        if (data.attack !== undefined && data.attack < 0) {
            errors.push({ field: 'attack', message: 'Attack time must be >= 0' });
        }
        if (data.decay !== undefined && data.decay < 0) {
            errors.push({ field: 'decay', message: 'Decay time must be >= 0' });
        }
        if (data.release !== undefined && data.release < 0) {
            errors.push({ field: 'release', message: 'Release time must be >= 0' });
        }
        if (data.minDistance !== undefined && data.minDistance < 0) {
            errors.push({ field: 'minDistance', message: 'Min distance must be >= 0' });
        }
        if (data.maxDistance !== undefined && data.maxDistance < 0) {
            errors.push({ field: 'maxDistance', message: 'Max distance must be >= 0' });
        }
        return errors;
    }


    // ── Public API ───────────────────────────────────────────

    /**
     * Play a tone by MIDI note number (0–127).
     * @param midiNote - MIDI note (60 = C4, 69 = A4)
     * @param opts - Optional overrides for duration, volume, waveform
     */
    playNote(midiNote: number, opts?: PlayToneOptions): void {
        const clamped = Math.round(Math.max(0, Math.min(127, midiNote)));
        const freq = midiToFrequency(clamped);
        this._playTone(freq, opts);
    }

    /**
     * Play a tone by frequency in Hz.
     * @param freq - Frequency (e.g., 440 for A4)
     * @param opts - Optional overrides for duration, volume, waveform
     */
    playFrequency(freq: number, opts?: PlayToneOptions): void {
        const clamped = Math.max(20, Math.min(20000, freq));
        this._playTone(clamped, opts);
    }

    /**
     * Stop all active voices with release envelope.
     */
    stop(): void {
        this._releaseAll();
    }

    // ── Internal ─────────────────────────────────────────────

    private _getAudioManager(): AudioManager | null {
        if (!this._audioManager) {
            this._audioManager = this.sceneObject.traceScene.getAudioManager();
        }
        return this._audioManager;
    }

    private _playDefault(): void {
        const freq = this._resolved.frequency ?? midiToFrequency(this._resolved.note ?? 60);
        this._playTone(freq);
    }

    private _playTone(freq: number, opts?: PlayToneOptions): void {
        const mgr = this._getAudioManager();
        if (!mgr) return;

        const ctx = mgr.getContext();
        const now = ctx.currentTime;

        const waveform = opts?.waveform ?? this._resolved.waveform ?? "sine";
        const volume = opts?.volume ?? this._resolved.volume ?? 0.5;
        const attack = this._resolved.attack ?? 0.01;
        const decay = this._resolved.decay ?? 0.1;
        const sustainLevel = this._resolved.sustain ?? 0.7;
        const release = this._resolved.release ?? 0.3;
        const duration = opts?.duration ?? this._resolved.duration ?? 0.5;
        const spatial = this._resolved.spatial ?? true;

        // Create oscillator
        const oscillator = ctx.createOscillator();
        oscillator.type = waveform;
        oscillator.frequency.setValueAtTime(freq, now);

        // Create gain node for ADSR envelope
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, now);
        // Attack: ramp to peak
        gain.gain.linearRampToValueAtTime(volume, now + attack);
        // Decay: ramp to sustain level
        gain.gain.linearRampToValueAtTime(volume * sustainLevel, now + attack + decay);

        // Connect: oscillator → gain → (panner?) → destination
        oscillator.connect(gain);

        let panner: PannerNode | null = null;
        if (spatial) {
            panner = ctx.createPanner();
            panner.panningModel = 'HRTF';
            panner.distanceModel = 'inverse';
            panner.refDistance = this._resolved.minDistance ?? 1;
            panner.maxDistance = this._resolved.maxDistance ?? 50;
            panner.rolloffFactor = this._resolved.rolloff ?? 1;

            // Set initial position from object transform
            const pos = this.sceneObject.transform.getAbsolutePosition();
            panner.positionX.setValueAtTime(pos.x, now);
            panner.positionY.setValueAtTime(pos.y, now);
            panner.positionZ.setValueAtTime(pos.z, now);

            gain.connect(panner);
            panner.connect(mgr.getDestination());

            // Start position sync observer if not already running
            this._ensurePositionObserver();
        } else {
            gain.connect(mgr.getDestination());
        }

        oscillator.start(now);

        const voice: Voice = { oscillator, gain, panner, releaseTimeout: null };
        this._activeVoices.add(voice);

        // Auto-release after duration (if > 0)
        if (duration > 0) {
            const releaseAt = now + attack + decay + duration;
            voice.releaseTimeout = window.setTimeout(() => {
                this._releaseVoice(voice);
            }, (attack + decay + duration) * 1000);

            // Schedule the sustain-hold → release envelope
            gain.gain.setValueAtTime(volume * sustainLevel, releaseAt);
            gain.gain.linearRampToValueAtTime(0, releaseAt + release);

            // Stop oscillator after release completes
            oscillator.stop(releaseAt + release + 0.01);
            oscillator.onended = () => {
                this._cleanupVoice(voice);
            };
        }
    }

    /**
     * Trigger release on a single voice.
     */
    private _releaseVoice(voice: Voice): void {
        if (!this._activeVoices.has(voice)) return;

        if (voice.releaseTimeout !== null) {
            clearTimeout(voice.releaseTimeout);
            voice.releaseTimeout = null;
        }

        const mgr = this._getAudioManager();
        if (!mgr) {
            this._cleanupVoice(voice);
            return;
        }

        const ctx = mgr.getContext();
        const now = ctx.currentTime;
        const release = this._resolved.release ?? 0.3;

        // Cancel scheduled values and apply release ramp
        voice.gain.gain.cancelScheduledValues(now);
        voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
        voice.gain.gain.linearRampToValueAtTime(0, now + release);

        voice.oscillator.stop(now + release + 0.01);
        voice.oscillator.onended = () => {
            this._cleanupVoice(voice);
        };
    }

    /**
     * Release all active voices.
     */
    private _releaseAll(): void {
        for (const voice of [...this._activeVoices]) {
            this._releaseVoice(voice);
        }
    }

    /**
     * Immediately stop and disconnect all voices without release.
     */
    private _stopAll(): void {
        for (const voice of [...this._activeVoices]) {
            if (voice.releaseTimeout !== null) {
                clearTimeout(voice.releaseTimeout);
            }
            try { voice.oscillator.stop(); } catch { /* already stopped */ }
            voice.oscillator.disconnect();
            voice.gain.disconnect();
            voice.panner?.disconnect();
        }
        this._activeVoices.clear();
        this._removePositionObserver();
    }

    /**
     * Remove a voice from tracking and disconnect its nodes.
     */
    private _cleanupVoice(voice: Voice): void {
        this._activeVoices.delete(voice);
        voice.oscillator.disconnect();
        voice.gain.disconnect();
        voice.panner?.disconnect();

        // Remove position observer if no spatial voices remain
        if (this._activeVoices.size === 0) {
            this._removePositionObserver();
        }
    }

    /**
     * Start a render observer that syncs all active PannerNodes to the object's world position.
     */
    private _ensurePositionObserver(): void {
        if (this._positionObserver || !this.scene) return;

        this._positionObserver = this.scene.onBeforeRenderObservable.add(() => {
            const pos = this.sceneObject.transform.getAbsolutePosition();
            const dx = pos.x - this._lastPosX;
            const dy = pos.y - this._lastPosY;
            const dz = pos.z - this._lastPosZ;
            if (dx * dx + dy * dy + dz * dz < 1e-6) return;
            this._lastPosX = pos.x;
            this._lastPosY = pos.y;
            this._lastPosZ = pos.z;
            for (const voice of this._activeVoices) {
                if (voice.panner) {
                    voice.panner.positionX.value = pos.x;
                    voice.panner.positionY.value = pos.y;
                    voice.panner.positionZ.value = pos.z;
                }
            }
        });
    }

    private _removePositionObserver(): void {
        if (this._positionObserver && this.scene) {
            this.scene.onBeforeRenderObservable.remove(this._positionObserver);
            this._positionObserver = null;
        }
    }
}
